import { flexRender, type Row, type Table } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import {
  formatMobileTableColumnLabel,
  getMobileTableSelectHeader,
  isMobileTableUtilityColumn,
  pickMobileTableTitleColumnId,
  shouldShowMobileTableDetailCell,
} from '~shared/components/table/mobile-table-cards.helpers';
import { MOBILE_TABLE_UTILITY_COLUMN_ID } from '~shared/components/table/mobile-table-cards.types';
import { cn } from '~shared/shadcn/lib/utils';

const EXPAND_COLUMN_ID = 'expand';

const isExpandColumn = (columnId: string) =>
  columnId.toLowerCase() === EXPAND_COLUMN_ID;

const isUtilityColumn = (columnId: string) =>
  isMobileTableUtilityColumn(columnId) || isExpandColumn(columnId);

function EnrollioTableMobileCard<TData>({
  row,
  className,
}: {
  row: Row<TData>;
  className?: string;
}) {
  const cells = row.getVisibleCells();
  const selectCell = cells.find(
    (cell) =>
      cell.column.id.toLowerCase() === MOBILE_TABLE_UTILITY_COLUMN_ID.SELECT,
  );
  const actionCell = cells.find(
    (cell) =>
      isMobileTableUtilityColumn(cell.column.id) &&
      cell.column.id.toLowerCase() !== MOBILE_TABLE_UTILITY_COLUMN_ID.SELECT,
  );
  const expandCell = cells.find((cell) => isExpandColumn(cell.column.id));
  const dataCells = cells.filter((cell) => !isUtilityColumn(cell.column.id));
  const titleColumnId = pickMobileTableTitleColumnId(
    dataCells.map((cell) => cell.column.id),
  );
  const titleCell = dataCells.find((cell) => cell.column.id === titleColumnId);
  const detailCells = dataCells.filter(
    (cell) =>
      cell.column.id !== titleColumnId &&
      shouldShowMobileTableDetailCell(cell.column.id, cell.getValue()),
  );

  return (
    <article
      className={cn('rounded-xl border border-border/50 p-3.5', className)}
    >
      <div className='mb-2 flex items-start gap-2'>
        <div className='min-w-0 flex-1'>
          {titleCell ? (
            <div className='break-words text-sm font-semibold leading-5 text-foreground [&>div]:px-0 [&_a]:whitespace-normal [&_a]:p-0 [&_a]:text-sm [&_a]:font-semibold [&_button]:h-auto [&_button]:whitespace-normal [&_button]:p-0 [&_button]:text-sm [&_button]:font-semibold'>
              {flexRender(
                titleCell.column.columnDef.cell,
                titleCell.getContext(),
              )}
            </div>
          ) : null}
        </div>
        {expandCell || actionCell || selectCell ? (
          <div className='flex shrink-0 items-center gap-1'>
            {expandCell ? (
              <div className='[&>div]:px-0'>
                {flexRender(
                  expandCell.column.columnDef.cell,
                  expandCell.getContext(),
                )}
              </div>
            ) : null}
            {actionCell ? (
              <div className='[&>div]:px-0'>
                {flexRender(
                  actionCell.column.columnDef.cell,
                  actionCell.getContext(),
                )}
              </div>
            ) : null}
            {selectCell ? (
              <div className='flex items-center [&>div]:px-0'>
                {flexRender(
                  selectCell.column.columnDef.cell,
                  selectCell.getContext(),
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {detailCells.length > 0 ? (
        <dl className='space-y-1.5'>
          {detailCells.map((cell) => {
            const label = formatMobileTableColumnLabel(cell.column.id);
            return (
              <div
                key={cell.id}
                className='flex items-start justify-between gap-3'
              >
                {label ? (
                  <dt className='w-[38%] shrink-0 text-sm leading-5 text-muted-foreground'>
                    {label}
                  </dt>
                ) : null}
                <dd className='flex min-w-0 flex-1 flex-col items-end break-words text-right text-sm font-normal leading-5 text-foreground [overflow-wrap:anywhere] [&>div]:ml-0 [&>div]:flex-wrap [&>div]:justify-end [&>div]:px-0 [&_a]:p-0 [&_button]:h-auto [&_button]:p-0'>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}
    </article>
  );
}

export function EnrollioTableMobile<TData>({
  table,
  isUpdating,
  emptyContent,
  expandedRow,
  loadingLabel = 'Updating table',
  rowClassName,
}: {
  table: Table<TData>;
  isUpdating: boolean;
  emptyContent: ReactNode;
  expandedRow?: (row: Row<TData>) => ReactNode;
  loadingLabel?: string;
  rowClassName?: string | ((row: Row<TData>) => string | undefined);
}) {
  const rows = table.getRowModel().rows;
  const selectHeader = getMobileTableSelectHeader(table);

  return (
    <div className='relative space-y-3 md:hidden'>
      {selectHeader && !selectHeader.isPlaceholder && rows.length > 0 ? (
        <div className='flex items-center gap-2 rounded-xl border border-border/50 px-3.5 py-2.5'>
          <div className='flex items-center [&>div]:px-0'>
            {flexRender(
              selectHeader.column.columnDef.header,
              selectHeader.getContext(),
            )}
          </div>
          <span className='text-sm font-medium'>Select all</span>
        </div>
      ) : null}
      {rows.length > 0 ? (
        rows.map((row) => (
          <Fragment key={row.id}>
            <EnrollioTableMobileCard
              row={row}
              className={
                typeof rowClassName === 'function'
                  ? rowClassName(row)
                  : rowClassName
              }
            />
            {expandedRow && row.getIsExpanded() ? (
              <div className='overflow-hidden rounded-xl border border-border/50'>
                {expandedRow(row)}
              </div>
            ) : null}
          </Fragment>
        ))
      ) : (
        <div className='py-8 text-center text-sm text-muted-foreground'>
          {isUpdating ? null : emptyContent}
        </div>
      )}
      {isUpdating ? (
        <div
          className='absolute inset-0 flex items-center justify-center bg-background/70'
          role='status'
        >
          <Loader2 className='size-7 animate-spin' />
          <span className='sr-only'>{loadingLabel}</span>
        </div>
      ) : null}
    </div>
  );
}
